import { ChangeEvent, forwardRef, useEffect, useRef, useState } from 'react';
import { Check, X, PencilLine } from 'lucide-react';
import { trpc } from '@/data-loaders';
import { Button } from '@curtin-dxp/curtin-ui';
import { IconComponent, NumberInput } from '@/components';
import { AcademicWorkloadAllocationsWithCalculations } from '#server/models/academic';
import { Input } from '@/components/ui/input';
import { formatHours, getHoursFromPercent } from '@/lib/utils';
import { DEFAULT_LEAVE_HOURS, MAX_ALLOCATABLE_HOURS } from '@/constants';
import { useHandleError, useHandleSuccess, useSyncedStateWithVersion } from '@/hooks';
import { useAwmsStore } from '@/store/AwmsStore';
import { SheetType } from '@/store/types';

interface AcademicWorkloadAllocationlProps {
  schoolId: number;
  allocationsWithCalculatedHours: AcademicWorkloadAllocationsWithCalculations;
  setAllocationsWithCalculatedHours: React.Dispatch<React.SetStateAction<AcademicWorkloadAllocationsWithCalculations>>;
  backendVersion: number;
  handleInvalidateAndRefetch: (schoolId: number, academicId: number) => Promise<void>;
  setHasSavedAcademicWorkload: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AcademicWorkloadAllocations = ({
  schoolId,
  allocationsWithCalculatedHours,
  setAllocationsWithCalculatedHours,
  backendVersion,
  handleInvalidateAndRefetch,
  setHasSavedAcademicWorkload,
}: AcademicWorkloadAllocationlProps) => {
  const setActiveSheet = useAwmsStore((state) => state.setActiveSheet);
  const setSelectedStaff = useAwmsStore((state) => state.setSelectedStaff);
  const defaultLeaveHoursFTEBased = allocationsWithCalculatedHours.fte * DEFAULT_LEAVE_HOURS;
  const [availableHours, setAvailableHours] = useState(0);
  const [leaveTotalHours, setLeaveTotalHours] = useState(0);
  const [engagementTotalHours, setEngamentTotalHours] = useState(0);
  const [isoltTotalHours, setIsoltTotalHours] = useState(0);
  const [leadershipTotalPercent, setLeadershipTotalPercent] = useState(0);
  const [leadershipTotalHours, setLeadershipTotalHours] = useState(0);
  const [researchHours, setResearchHours] = useState(0);
  const [researchTotalHours, setResearchTotalHours] = useState(0);
  const [teachingTotalPercent, setTeachingTotalPercent] = useState(0);
  const [teachingTotalHours, setTeachingTotalHours] = useState(0);
  const [teachingCapacityTotalPercent, setTeachingCapacityTotalPercent] = useState(0);
  const [teachingCapacityTotalHours, setTeachingCapacityTotalHours] = useState(0);
  const [tdTotalHours, setTdTotalHours] = useState(0);
  const [tdTotalPercent, setTdTotalPercent] = useState(0);
  const [trdTotalHours, setTrdTotalHours] = useState(0);
  const [trdTotalPercent, setTrdTotalPercent] = useState(0);
  const [isEditable, setIsEditable] = useState(false);
  const activeYear = useAwmsStore((state) => state.activeYear);
  const handleError = useHandleError();
  const sendToastAcademicWorkloadAllocations = useHandleSuccess({
    title: 'Update academic workload allocations',
  });
  // keep local state of allocations (to be used for storing edited values)
  const [allocationsState, setAllocationsState, resetLocalState] = useSyncedStateWithVersion(
    allocationsWithCalculatedHours,
    backendVersion,
  );
  const tableHeaderStyle = `table-cell w-[150px]  border-collapse bg-[#F1EEFD] py-[10px] pl-[20px] pr-[10px]
    text-left text-b2 font-semibold text-secondary-grey-02 border-b-[0.5px] border-b-deep-blue whitespace-nowrap box-content `;
  const tableTopSubHeaderStyle = `table-cell w-[150px]  border-collapse py-[10px] pl-[20px] pr-[10px] text-left text-b2
    text-primary-black-01 font-semibold whitespace-nowrap border-b-[0.5px] border-b-secondary-grey-03 box-content `;
  const tableRowStyle = `table-cell align-top w-[150px] w-auto border-collapse py-[10px] pl-[20px] pr-[10px] text-left text-xs
    text-secondary-grey-02 font-semibold whitespace-nowrap bg-white leading-5 box-content `;

  const { isLoading: isUpdating, mutate: updateAcademicWorkloadAllocationMutation } =
    trpc.academic.updateAcademicWorkloadAllocations.useMutation({
      onSuccess: async () => {
        setHasSavedAcademicWorkload(true);
        await handleInvalidateAndRefetch(schoolId, allocationsWithCalculatedHours.id);
        sendToastAcademicWorkloadAllocations();
      },
      onError: (error) => {
        handleError([error]);
      },
    });

  // Recalculate all the total percentage and hours
  useEffect(() => {
    const annualLeaveHours =
      allocationsState.leaveAnnualHours > 0
        ? allocationsState.leaveAnnualHours
        : allocationsState.fte * DEFAULT_LEAVE_HOURS;
    const leaveTotalHours = annualLeaveHours + allocationsState.leaveOtherHours + allocationsState.leaveAspHours;
    const availableHours = MAX_ALLOCATABLE_HOURS * allocationsState.fte - leaveTotalHours;
    setAvailableHours(availableHours);
    setLeaveTotalHours(leaveTotalHours);
    setEngamentTotalHours((allocationsState.engagementPercent / 100) * availableHours);
    setIsoltTotalHours((allocationsState.isoltPercent / 100) * availableHours);
    const totalLeadershipPercent =
      allocationsState.leadershipCourseCoordinatorPercent +
      allocationsState.leadershipDeanPercent +
      allocationsState.leadershipDhosPercent +
      allocationsState.leadershipDirectorPercent +
      allocationsState.leadershipDisciplineLeadPercent +
      allocationsState.leadershipHosPercent +
      allocationsState.leadershipMajorCoordinationPercent +
      allocationsState.leadershipOtherPercent;
    setLeadershipTotalPercent(totalLeadershipPercent);
    setLeadershipTotalHours((totalLeadershipPercent / 100) * availableHours);
    const researchHours =
      (allocationsState.researchPercent / 100) * availableHours - allocationsState.researchHdrTotalHours;
    setResearchHours(researchHours);
    const researchTotalHours = (allocationsState.researchPercent / 100) * availableHours;
    setResearchTotalHours(researchTotalHours);
    setTeachingTotalPercent(allocationsState.teachingPercent);
    setTeachingTotalHours((allocationsState.teachingPercent / 100) * availableHours);
    const tdTotalHours =
      allocationsState.tdUnitAllocationHours +
      allocationsState.tdShortFormLearningHours +
      allocationsState.tdOtherHours;
    setTdTotalHours(tdTotalHours);
    setTdTotalPercent(availableHours === 0 ? 0 : (tdTotalHours / availableHours) * 100);
    const trdTotalHours =
      allocationsState.trdUnitAllocationHours +
      allocationsState.trdNewUnitDevelopmentHours +
      allocationsState.trdOtherHours +
      allocationsState.trdHdrTotalHours;
    setTrdTotalHours(trdTotalHours);
    setTrdTotalPercent(availableHours === 0 ? 0 : (trdTotalHours / availableHours) * 100);
  }, [allocationsState]);

  useEffect(() => {
    // Recalculate teaching capacity total percentage and hours
    const teachingCapacityTotalHours = teachingTotalHours - tdTotalHours - trdTotalHours;
    setTeachingCapacityTotalHours(teachingCapacityTotalHours);
    setTeachingCapacityTotalPercent(availableHours === 0 ? 0 : (teachingCapacityTotalHours / availableHours) * 100);
  }, [tdTotalHours, trdTotalHours, availableHours]);

  useEffect(() => {
    // Recalculate total assigned hours and update the total in the left panel
    const totalAssignedHours =
      leadershipTotalHours + researchTotalHours + isoltTotalHours + teachingTotalHours + engagementTotalHours;
    setAllocationsWithCalculatedHours((prev) => ({
      ...prev,
      totalAssignedHours,
      availableHours,
    }));
  }, [availableHours, leadershipTotalHours, researchTotalHours, isoltTotalHours, teachingTotalHours]);

  const handleRedirect = (activeSheet: string) => {
    setSelectedStaff({
      id: allocationsState.id,
      firstName: allocationsState.firstName,
      lastName: allocationsState.lastName,
    });
    setActiveSheet(activeSheet as SheetType);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, dataset } = event.target;
    const fieldName = dataset.fieldName || '';
    if (value.trim() === '' || isNaN(Number(value))) {
      return;
    }

    setAllocationsState((prev) => ({
      ...prev,
      [fieldName]: Number(value),
    }));
  };

  const handleUpdate = () => {
    const updatedAllocationsDetails = {
      id: allocationsState.id,
      leaveAnnualHours: allocationsState.leaveAnnualHours,
      leaveOtherHours: allocationsState.leaveOtherHours,
      leaveAspHours: allocationsState.leaveAspHours,
      leadershipCourseCoordinatorPercent: allocationsState.leadershipCourseCoordinatorPercent,
      leadershipDeanPercent: allocationsState.leadershipDeanPercent,
      leadershipDhosPercent: allocationsState.leadershipDhosPercent,
      leadershipDirectorPercent: allocationsState.leadershipDirectorPercent,
      leadershipDisciplineLeadPercent: allocationsState.leadershipDisciplineLeadPercent,
      leadershipHosPercent: allocationsState.leadershipHosPercent,
      leadershipMajorCoordinationPercent: allocationsState.leadershipMajorCoordinationPercent,
      leadershipOtherPercent: allocationsState.leadershipOtherPercent,
      researchPercent: allocationsState.researchPercent,
      isoltPercent: allocationsState.isoltPercent,
      teachingPercent: allocationsState.teachingPercent,
      tdShortFormLearningHours: allocationsState.tdShortFormLearningHours,
      tdOtherHours: allocationsState.tdOtherHours,
      trdNewUnitDevelopmentHours: allocationsState.trdNewUnitDevelopmentHours,
      trdOtherHours: allocationsState.trdOtherHours,
    };
    updateAcademicWorkloadAllocationMutation({
      academicWorkloadAllocations: updatedAllocationsDetails,
      schoolId,
      year: activeYear,
    });
  };

  return (
    <div className="grow basis-3/5 overflow-auto bg-white py-8 pl-[35px] pr-3">
      <div className="flex flex-row items-center gap-4 pb-4">
        <div className="text-sh3 font-bold">Edit staff workload allocations</div>
        {isEditable ? (
          <div className="flex flex-row gap-1">
            <Button
              size="icon"
              variant="filled-secondary"
              className="h-8 w-8 flex-shrink-0"
              onClick={() => {
                setIsEditable(false);
                resetLocalState();
              }}
            >
              <IconComponent IconType={X} />
            </Button>{' '}
            <Button
              size="icon"
              variant="filled-secondary"
              className="h-8 w-8 flex-shrink-0"
              onClick={() => {
                setIsEditable(false);
                handleUpdate();
              }}
            >
              <IconComponent IconType={Check} />
            </Button>
          </div>
        ) : (
          <Button
            size="icon"
            variant="filled-secondary"
            className={`h-8 w-8 ${isUpdating ? 'cursor-wait' : 'cursor-pointer'}`}
            onClick={() => {
              setIsEditable(true);
            }}
          >
            <IconComponent IconType={PencilLine} />
          </Button>
        )}
      </div>
      <table className="table-auto">
        <thead className="table-header-group">
          <tr>
            <th key="header-leave" className={`${tableHeaderStyle} bg-[#F1EEFD] pl-10`}>
              Leave
            </th>
            <th key="header-engagement" className={`${tableHeaderStyle} bg-[#F1EEFD]`}>
              Engagement
            </th>
            <th key="header-isolt" className={`${tableHeaderStyle} bg-[#F1EEFD]`}>
              ISoLT
            </th>
            <th key="header-leadership" className={`${tableHeaderStyle} bg-[#F1EEFD]`}>
              Leadership
            </th>
            <th key="header-research" className={`${tableHeaderStyle} bg-[#F1EEFD]`}>
              Research
            </th>
            <th key="header-teaching" className={`${tableHeaderStyle} border-r-[1px] border-r-deep-blue bg-[#F1EEFD]`}>
              Teaching
            </th>
            <th key="header-teaching-capacity" className={`${tableHeaderStyle} bg-[#FFFADC]`}>
              Teaching capacity
            </th>
            <th key="header-td" className={`${tableHeaderStyle} bg-[#FFFADC]`}>
              TD
            </th>
            <th key="header-trd" className={`${tableHeaderStyle} bg-[#FFFADC]`}>
              TRD
            </th>
          </tr>
        </thead>
        <tbody>
          <tr key="subheader-row" className="table-row bg-primary-website-background-colour">
            <td key="subheader-cell-leave" className={`${tableTopSubHeaderStyle} pl-10`}>
              {formatHours(leaveTotalHours, true, 2)} <br />
            </td>
            <td key="subheader-cell-engagement" className={`${tableTopSubHeaderStyle}`}>
              {formatHours(allocationsState.engagementPercent, false, 2)}% <br />
              <span className="text-b3 text-secondary-grey-02">{formatHours(engagementTotalHours, true, 2)}</span>
            </td>
            <td key="subheader-cell-isolt" className={`${tableTopSubHeaderStyle}`}>
              {formatHours(allocationsState.isoltPercent, false, 2)}% <br />
              <span className="text-b3 text-secondary-grey-02">{formatHours(isoltTotalHours, true, 2)}</span>
            </td>
            <td key="subheader-cell-leadership" className={`${tableTopSubHeaderStyle}`}>
              {formatHours(leadershipTotalPercent, false, 2)}% <br />
              <span className="text-b3 text-secondary-grey-02">{formatHours(leadershipTotalHours, true, 2)}</span>
            </td>
            <td key="subheader-cell-research" className={`${tableTopSubHeaderStyle}`}>
              {formatHours(allocationsState.researchPercent, false, 2)}% <br />
              <span className="text-b3 text-secondary-grey-02">{formatHours(researchTotalHours, true, 2)}</span>
            </td>
            <td key="subheader-cell-teaching" className={`${tableTopSubHeaderStyle} border-r-[1px] border-r-deep-blue`}>
              {formatHours(teachingTotalPercent, false, 2)}% <br />
              <span className="text-b3 text-secondary-grey-02">{formatHours(teachingTotalHours, true, 2)}</span>
            </td>
            <td
              key="subheader-cell-teaching-capacity"
              className={`${tableTopSubHeaderStyle} border-b-[0.5px] border-deep-blue
                ${teachingCapacityTotalPercent < 0 ? 'text-tertiary-red' : ''}`}
            >
              {formatHours(teachingCapacityTotalPercent, false, 2)}% <br />
              <span
                className={`text-b3 text-secondary-grey-02 ${teachingCapacityTotalHours < 0 ? 'text-tertiary-red' : ''}`}
              >
                {formatHours(teachingCapacityTotalHours, true, 2)}
              </span>
            </td>
            <td key="subheader-cell-td" className={`${tableTopSubHeaderStyle}`}>
              {formatHours(tdTotalPercent, false, 2)}% <br />
              <span className="text-b3 text-secondary-grey-02">{formatHours(tdTotalHours, true, 2)}</span>
            </td>
            <td key="subheader-cell-trd" className={`${tableTopSubHeaderStyle}`}>
              {formatHours(trdTotalPercent, false, 2)}% <br />
              <span className="text-b3 text-secondary-grey-02">{formatHours(trdTotalHours, true, 2)}</span>
            </td>
          </tr>
          <tr key="details-row" className="table-row bg-primary-website-background-colour">
            <td key="details-row-cell-leave" className={`${tableRowStyle}`}>
              <div className="flex max-w-[140px] flex-col py-3">
                <div className="mb-1">
                  <span className="text-xs font-semibold text-secondary-grey-01">Annual leave (hour)</span>
                  <br />
                  <span
                    className={`text-xs font-bold text-secondary-grey-01 ${allocationsState.leaveAnnualHours > 0 ? 'line-through' : ''}`}
                  >
                    {formatHours(defaultLeaveHoursFTEBased, true, 2)}
                  </span>
                </div>

                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Override</span>
                  <br />
                  <NumberInput
                    value={allocationsState.leaveAnnualHours}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="leaveAnnualHours"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />
                </div>

                <div className="mb-5">
                  <span className="text-primary-black-01 text-xs font-semibold text-secondary-grey-01">
                    Other (hour)
                  </span>
                  <NumberInput
                    value={allocationsState.leaveOtherHours}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="leaveOtherHours"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />
                </div>

                <div>
                  <span className="text-primary-black-01 whitespace-break-spaces text-xs font-semibold text-secondary-grey-01">
                    Higher Degree Support (ASP) (hour)
                    <NumberInput
                      value={allocationsState.leaveAspHours}
                      className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                      disabled={!isEditable}
                      data-field-name="leaveAspHours"
                      onChange={handleInputChange}
                      decimalScale={2}
                    />
                  </span>
                </div>
              </div>
            </td>
            <td key="details-row-cell-engagement" className={`${tableRowStyle}`}></td>
            <td key="details-row-cell-isolt" className={`${tableRowStyle}`}>
              <div className="flex flex-col py-3">
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">ISoLT (%)</span>
                  <NumberInput
                    value={allocationsState.isoltPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="isoltPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />
                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(isoltTotalHours, true, 2)}
                  </span>
                </div>
              </div>
            </td>
            <td key="details-row-cell-leadership" className={`${tableRowStyle}`}>
              <div className="flex max-w-[140px] flex-col py-3">
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Course coordination (%)</span>
                  <NumberInput
                    value={allocationsState.leadershipCourseCoordinatorPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="leadershipCourseCoordinatorPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />

                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(
                      getHoursFromPercent(allocationsState.leadershipCourseCoordinatorPercent, availableHours),
                      true,
                      2,
                    )}
                  </span>
                </div>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Dean (%)</span>
                  <NumberInput
                    value={allocationsState.leadershipDeanPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="leadershipDeanPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />

                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(getHoursFromPercent(allocationsState.leadershipDeanPercent, availableHours), true, 2)}
                  </span>
                </div>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">DHoS (%)</span>
                  <NumberInput
                    value={allocationsState.leadershipDhosPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="leadershipDhosPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />
                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(getHoursFromPercent(allocationsState.leadershipDhosPercent, availableHours), true, 2)}
                  </span>
                </div>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Director (%)</span>
                  <NumberInput
                    value={allocationsState.leadershipDirectorPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="leadershipDirectorPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />

                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(
                      getHoursFromPercent(allocationsState.leadershipDirectorPercent, availableHours),
                      true,
                      2,
                    )}
                  </span>
                </div>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Discipline Lead (%)</span>
                  <NumberInput
                    value={allocationsState.leadershipDisciplineLeadPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="leadershipDisciplineLeadPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />

                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(
                      getHoursFromPercent(allocationsState.leadershipDisciplineLeadPercent, availableHours),
                      true,
                      2,
                    )}
                  </span>
                </div>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Hos (%)</span>
                  <NumberInput
                    value={allocationsState.leadershipHosPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="leadershipHosPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />

                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(getHoursFromPercent(allocationsState.leadershipHosPercent, availableHours), true, 2)}
                  </span>
                </div>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Major coordination (%)</span>
                  <NumberInput
                    value={allocationsState.leadershipMajorCoordinationPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="leadershipMajorCoordinationPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />

                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(
                      getHoursFromPercent(allocationsState.leadershipMajorCoordinationPercent, availableHours),
                      true,
                      2,
                    )}
                  </span>
                </div>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Other leadership (%)</span>
                  <NumberInput
                    value={allocationsState.leadershipOtherPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="leadershipOtherPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />

                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(getHoursFromPercent(allocationsState.leadershipOtherPercent, availableHours), true, 2)}
                  </span>
                </div>
              </div>
            </td>
            <td key="details-row-cell-research" className={`${tableRowStyle}`}>
              <div className="flex flex-col py-3">
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Research (%)</span>
                  <NumberInput
                    value={allocationsState.researchPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="researchPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />
                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(researchHours, true, 2)}
                  </span>
                </div>
                <div className="mb-5">
                  <Button
                    onClick={() => handleRedirect('hdr')}
                    className="m-0 w-fit border-none p-0 text-left text-xs font-semibold text-deep-blue underline"
                    variant={null}
                  >
                    Adjust Research HDR
                  </Button>
                  <br />
                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(allocationsState.researchHdrTotalHours, true, 2)}
                  </span>
                </div>
              </div>
            </td>
            <td key="details-row-cell-teaching" className={`${tableRowStyle} border-r-[1px] border-r-deep-blue`}>
              <div className="flex flex-col py-3">
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Teaching (%)</span>
                  <NumberInput
                    value={allocationsState.teachingPercent}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="teachingPercent"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />
                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(getHoursFromPercent(allocationsState.teachingPercent, availableHours), true, 2)}
                  </span>
                </div>
              </div>
            </td>
            <td key="details-row-cell-teaching-capacity" className={`${tableRowStyle}`}></td>
            <td key="details-row-cell-td" className={`${tableRowStyle}`}>
              <div className="flex flex-col py-3">
                <div className="mb-10">
                  <Button
                    onClick={() => handleRedirect('staff')}
                    className="m-0 w-fit border-none p-0 text-left text-xs font-semibold text-deep-blue underline"
                    variant={null}
                  >
                    TD from unit allocation
                  </Button>
                  <br />
                  <span className="mb-10 text-xs font-normal text-secondary-grey-02">
                    {formatHours(allocationsState.tdUnitAllocationHours, true, 2)}
                  </span>
                </div>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Short form learning (hour)</span>
                  <NumberInput
                    value={allocationsState.tdShortFormLearningHours}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="tdShortFormLearningHours"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />
                </div>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">Other TD (hour)</span>
                  <NumberInput
                    value={allocationsState.tdOtherHours}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="tdOtherHours"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />
                </div>
              </div>
            </td>
            <td key="details-row-cell-trd" className={`${tableRowStyle}`}>
              <div className="flex flex-col py-3">
                <div className="mb-10">
                  <Button
                    onClick={() => handleRedirect('staff')}
                    className="m-0 w-fit border-none p-0 text-left text-xs font-semibold text-deep-blue underline"
                    variant={null}
                  >
                    TRD from unit allocation
                  </Button>
                  <br />
                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(allocationsState.trdUnitAllocationHours, true, 2)}
                  </span>
                </div>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-secondary-grey-01">New unit development (hour)</span>
                  <NumberInput
                    value={allocationsState.trdNewUnitDevelopmentHours}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="trdNewUnitDevelopmentHours"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />
                </div>
                <div className="mb-10">
                  <span className="text-xs font-semibold text-secondary-grey-01">Other TRD (hour)</span>
                  <NumberInput
                    value={allocationsState.trdOtherHours}
                    className="h-[30px] w-[117px] bg-white text-xs font-normal leading-5"
                    disabled={!isEditable}
                    data-field-name="trdOtherHours"
                    onChange={handleInputChange}
                    decimalScale={2}
                  />
                </div>
                <div className="mb-5">
                  <Button
                    onClick={() => handleRedirect('hdr')}
                    className="m-0 w-fit border-none p-0 text-left text-xs font-semibold text-deep-blue underline"
                    variant={null}
                  >
                    Adjust TRD HDR
                  </Button>
                  <br />
                  <span className="text-xs font-normal text-secondary-grey-02">
                    {formatHours(allocationsState.trdHdrTotalHours, true, 2)}
                  </span>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
